import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton,
    makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AnalyticsTable, { CellWithTooltip, AnalyticsSelectFilter } from 'components/AnalyticsTable';
import { applyFilters, applySorting, paginate } from 'components/AnalyticsTable/dataUtils';
import { calcPctChange, buildPctChangeColumn } from 'components/AnalyticsTable/PctChangeColumn';
import { getEventCallComparison } from 'modules/api/usageAnalytics';

const useStyles = makeStyles((theme) => ({
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: theme.spacing(1),
    },
    identifier: {
        fontSize: 13,
        color: '#666',
        marginTop: 2,
    },
    content: {
        height: 500,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
}));

function pctAccessor(row) {
    return calcPctChange(row.currentMonthTotal, row.previousMonthTotal);
}

function buildColumns(data, currentMonthLabel, previousMonthLabel) {
    const methodOptions = data
        ? [...new Set(data.map((r) => r.eventCall).filter(Boolean))].sort()
        : [];

    return [
        {
            accessor: 'eventCall',
            Header: 'Metodas',
            width: 200,
            Filter: AnalyticsSelectFilter,
            filterOptions: methodOptions,
            Cell: (props) => <CellWithTooltip value={props.cell.value} />,
        },
        {
            accessor: 'currentMonthTotal',
            Header: currentMonthLabel,
            width: 120,
            disableFilters: true,
        },
        {
            accessor: 'previousMonthTotal',
            Header: previousMonthLabel,
            width: 120,
            disableFilters: true,
        },
        buildPctChangeColumn({
            accessor: pctAccessor,
        }),
    ];
}

export default function ComparisonDialog({ open, onClose, identifier, monthStart, compareMonth }) {
    const classes = useStyles();
    const cache = useRef({ key: null, data: null });
    const [fullData, setFullData] = useState(null);

    const currentMonthLabel = dayjs(monthStart).locale('lt').format('MMMM YYYY');
    const previousMonthLabel = dayjs(compareMonth).locale('lt').format('MMMM YYYY');

    useEffect(() => {
        if (!open) {
            cache.current = { key: null, data: null };
            setFullData(null);
        }
    }, [open]);

    const columns = useMemo(
        () => buildColumns(fullData, currentMonthLabel, previousMonthLabel),
        [fullData, currentMonthLabel, previousMonthLabel],
    );

    const loadData = useCallback(
        (query, rawFilters) => {
            if (!identifier) return Promise.resolve({ data: [], itemCount: 0 });

            const cacheKey = `${identifier}:${monthStart}`;
            const cached = cache.current.key === cacheKey ? cache.current.data : null;

            const fetchPromise = cached
                ? Promise.resolve(cached)
                : getEventCallComparison(identifier, monthStart).then((data) => {
                      cache.current = { key: cacheKey, data };
                      setFullData(data);
                      return data;
                  });

            return fetchPromise.then((allData) => {
                let filtered = rawFilters?.length ? applyFilters(allData, rawFilters) : allData;
                filtered = applySorting(filtered, query.sort, (row, field) =>
                    field === 'pctChange' ? pctAccessor(row) ?? -Infinity : undefined,
                );
                return paginate(filtered, query.page, query.pageSize);
            });
        },
        [identifier, monthStart],
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle disableTypography className={classes.title}>
                <div>
                    <Typography variant="h6">Užklausų palyginimas</Typography>
                    <Typography className={classes.identifier}>{identifier}</Typography>
                </div>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.content}>
                {open && identifier && (
                    <AnalyticsTable
                        columns={columns}
                        loadData={loadData}
                        getRowID={(row) => row.eventCall}
                        pageSizeOptions={[20, 50, 100]}
                        tableMinWidth={0}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
